import { useEffect, useState } from "react";
import { Loader2, MapPin, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, digits, maskCep } from "@/lib/api";
import { normCity } from "@/lib/boyCourier";
import { NextBtn, StepHead } from "./StepBar";

const isCep = (v) => /^\d/.test(v.trim());

function Selected({ f, onChange }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border-2 border-primary bg-primary/5 p-4" data-testid="boy-region-selected">
      <MapPin className="h-6 w-6 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold" data-testid="boy-region-city">{f.city} - {f.state}</p>
        {f.cep && <p className="text-xs text-muted-foreground">CEP {maskCep(f.cep)}</p>}
      </div>
      <Button type="button" size="sm" variant="ghost" onClick={onChange} data-testid="boy-region-change">Trocar</Button>
    </div>
  );
}

export function StepRegion({ f, patch, info, onNext }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const k = q.trim();
    if (isCep(k) || k.length < 2) {
      setList([]);
      return undefined;
    }
    const id = setTimeout(() => {
      api.get("/public/br-cities", { params: { q: k } }).then((r) => setList(r.data)).catch(() => setList([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  const choose = (c, cep = "") => {
    patch({ city: c.city, state: c.state, cep });
    setQ("");
    setList([]);
  };
  const byCep = async (d) => {
    setBusy(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`).then((x) => x.json());
      if (r.erro) return toast.error("CEP não encontrado");
      const { data } = await api.get("/public/br-cities", { params: { q: r.localidade } });
      const hit = data.find((c) => c.state === r.uf && normCity(c.city) === normCity(r.localidade));
      if (hit) choose(hit, d);
      else setQ(r.localidade);
    } catch {
      toast.error("Não foi possível buscar o CEP agora. Digite o nome da cidade.");
    } finally {
      setBusy(false);
    }
  };
  const onType = (v) => {
    const val = isCep(v) ? maskCep(v) : v;
    setQ(val);
    if (isCep(val) && digits(val).length === 8) byCep(digits(val));
  };
  const go = (e) => {
    e.preventDefault();
    if (!f.city) return toast.error("Escolha a cidade onde você vai rodar");
    onNext();
  };
  return (
    <form onSubmit={go} className="space-y-5" data-testid="boy-step-region">
      <StepHead title="Onde você quer rodar?" text="Busque pelo nome da cidade ou digite o CEP." />
      {f.city ? <Selected f={f} onChange={() => patch({ city: "", state: "", cep: "" })} /> : (
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => onType(e.target.value)} placeholder="Cidade ou CEP" className="h-12 pl-9 text-base" autoComplete="off" data-testid="boy-region-search-input" />
            {busy && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin" />}
            {!!list.length && (
              <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl border bg-popover py-1 shadow-lg" data-testid="boy-region-suggestions">
                {list.map((c, i) => (
                  <li key={`${c.city}-${c.state}`}>
                    <button type="button" onClick={() => choose(c)} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent" data-testid={`boy-region-option-${i}`}>
                      <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />{c.city}<span className="text-muted-foreground">- {c.state}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {!q && info?.city && (
            <button type="button" onClick={() => setQ(info.city)} className="rounded-full border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent" data-testid="boy-region-suggest-store">
              Sugestão: {info.city}{info.state && ` - ${info.state}`}
            </button>
          )}
        </div>
      )}
      <NextBtn disabled={!f.city} testid="boy-step-region-next">Avançar</NextBtn>
    </form>
  );
}
